import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { database } from '../firebase';
import { ref, push } from 'firebase/database';
import guestGroups from '../data/guestGroups';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const PageBorder = styled.div`
  border: 2px solid #000;
  padding: 3.5rem 3rem;
  margin: 1rem;
  max-width: 600px;
  width: 100%;
  background-color: #ffffff;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem;
    margin: 0.5rem;
  }
`;

const FormContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const ScriptTitle = styled.h1`
  font-family: 'Great Vibes', cursive;
  font-size: 3.5rem;
  font-weight: 400;
  color: #000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.div`
  font-family: 'Cinzel', serif;
  font-size: 0.85rem;
  letter-spacing: 3px;
  color: #000;
  text-transform: uppercase;
  margin-top: 0.5rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #000;
  width: 60px;
  margin: 1.25rem auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Label = styled.label`
  font-family: 'Cinzel', serif;
  font-size: 0.75rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000;
  font-weight: 500;
`;

const FieldNote = styled.p`
  font-family: 'Cinzel', serif;
  font-size: 0.7rem;
  line-height: 1.5;
  letter-spacing: 0.5px;
  color: #333;
  font-style: italic;
  margin: -0.15rem 0 0;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem;
  border: none;
  border-bottom: 1px solid #000;
  font-family: 'Cinzel', serif;
  font-size: 0.95rem;
  letter-spacing: 1px;
  color: #000;
  background: transparent;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-bottom-color: #555;
  }

  &::placeholder {
    color: #aaa;
    font-style: italic;
    letter-spacing: 0.5px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
`;

const SuggestionList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: #fff;
  border: 1px solid #000;
  border-top: none;
  max-height: 260px;
  overflow-y: auto;
`;

const SuggestionItem = styled.li`
  padding: 0.85rem 0.75rem;
  font-family: 'Cinzel', serif;
  font-size: 0.9rem;
  letter-spacing: 1px;
  color: #000;
  cursor: pointer;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  ${props => props.$highlighted && `
    background-color: #000;
    color: #fff;
  `}

  @media (hover: hover) {
    &:hover {
      background-color: #000;
      color: #fff;
    }
  }
`;

const SuggestionParty = styled.span`
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  font-style: italic;
  color: inherit;
  opacity: 0.7;
  margin-top: 0.2rem;
`;

const NoResults = styled.li`
  padding: 0.85rem 0.75rem;
  font-family: 'Cinzel', serif;
  font-size: 0.8rem;
  letter-spacing: 1px;
  font-style: italic;
  color: #555;
`;

const PartyContainer = styled.div`
  border: 1px solid #000;
  padding: 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const PartyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
`;

const PartyTitle = styled.div`
  font-family: 'Cinzel', serif;
  font-size: 0.75rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000;
  font-weight: 500;
`;

const ChangeButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: 'Cinzel', serif;
  font-size: 0.7rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #000;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #555;
  }
`;

const GuestRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const GuestName = styled.div`
  font-family: 'Cinzel', serif;
  font-size: 0.95rem;
  letter-spacing: 1px;
  color: #000;
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border: 1px solid #000;
  font-family: 'Cinzel', serif;
  font-size: 0.95rem;
  letter-spacing: 1px;
  color: #000;
  min-height: 80px;
  resize: vertical;
  background: transparent;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #555;
  }

  &::placeholder {
    color: #aaa;
    font-style: italic;
    letter-spacing: 0.5px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 0.5rem;
  justify-content: flex-start;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Cinzel', serif;
  font-size: 0.8rem;
  letter-spacing: 1.5px;
  color: #000;
  cursor: pointer;
  text-transform: uppercase;

  input[type="radio"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #000;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background-color: #000;
  color: #fff;
  border: 2px solid #000;
  font-family: 'Cinzel', serif;
  font-size: 0.8rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  align-self: center;
  min-width: 220px;

  &:hover {
    background-color: #fff;
    color: #000;
  }

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
    color: #fff;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem 1rem;

  h2 {
    font-family: 'Great Vibes', cursive;
    font-size: 2.5rem;
    font-weight: 400;
    color: #000;
    margin-bottom: 1rem;
  }

  p {
    font-family: 'Cinzel', serif;
    font-size: 0.85rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #000;
    line-height: 2;
  }
`;

const HomeButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background-color: #000;
  color: #fff;
  border: 2px solid #000;
  font-family: 'Cinzel', serif;
  font-size: 0.8rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  min-width: 220px;
  box-sizing: border-box;
  text-align: center;

  &:hover {
    background-color: #fff;
    color: #000;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  border: 1px solid #000;
  font-family: 'Cinzel', serif;
  font-size: 0.8rem;
  letter-spacing: 1px;
  color: #000;
  text-align: center;
`;

// Flat index of every searchable invitee name -> their party.
// Generic "Guest" placeholders stay in the party display but are not searchable.
const searchIndex = guestGroups.flatMap((group, groupIndex) =>
  group
    .filter(name => name.toLowerCase() !== 'guest')
    .map(name => ({ name, groupIndex }))
);

function RSVPPage() {
  const [formData, setFormData] = useState({
    dietaryRestrictions: '',
    message: ''
  });
  const [nameQuery, setNameQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(null);
  const [guestResponses, setGuestResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const query = nameQuery.trim().toLowerCase();
  const suggestions = query
    ? searchIndex.filter(entry => entry.name.toLowerCase().includes(query)).slice(0, 8)
    : [];

  const selectedGroup = selectedGroupIndex !== null ? guestGroups[selectedGroupIndex] : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleQueryChange = (e) => {
    setNameQuery(e.target.value);
    setShowSuggestions(true);
    setHighlightIndex(-1);
    setError('');
  };

  const handleSelectGroup = (groupIndex) => {
    setSelectedGroupIndex(groupIndex);
    setGuestResponses({});
    setNameQuery('');
    setShowSuggestions(false);
    setHighlightIndex(-1);
    setError('');
  };

  const handleClearGroup = () => {
    setSelectedGroupIndex(null);
    setGuestResponses({});
    setNameQuery('');
    setError('');
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const choice = highlightIndex >= 0 ? suggestions[highlightIndex] : suggestions[0];
      if (choice) handleSelectGroup(choice.groupIndex);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleGuestResponse = (guestName, value) => {
    setGuestResponses(prev => ({
      ...prev,
      [guestName]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!selectedGroup) {
      setError('Please search for your name and select your party');
      return;
    }
    const unanswered = selectedGroup.filter(name => !guestResponses[name]);
    if (unanswered.length > 0) {
      setError(`Please respond for each guest: ${unanswered.join(', ')}`);
      return;
    }

    const attendingCount = selectedGroup.filter(name => guestResponses[name] === 'attending').length;

    setIsSubmitting(true);

    try {
      const rsvpRef = ref(database, 'rsvps');
      await push(rsvpRef, {
        ...formData,
        name: selectedGroup.join(', '),
        attendance: attendingCount > 0 ? 'attending' : 'declining',
        numberOfGuests: attendingCount > 0 ? String(attendingCount) : '',
        guests: selectedGroup.map(name => ({
          name,
          attendance: guestResponses[name]
        })),
        timestamp: new Date().toISOString()
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      setError('Failed to submit RSVP. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <AppContainer>
        <PageBorder>
          <FormContainer>
            <Header>
              <ScriptTitle>Thank You</ScriptTitle>
              <Divider />
              <Subtitle>YOUR RSVP HAS BEEN RECEIVED</Subtitle>
            </Header>
            <SuccessMessage>
              <p>We look forward to celebrating with you!</p>
              <HomeButton to="/">Back to Website</HomeButton>
            </SuccessMessage>
          </FormContainer>
        </PageBorder>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <PageBorder>
        <FormContainer>
          <Header>
            <ScriptTitle>Kindly reply</ScriptTitle>
            <Subtitle>BY THE FOURTH OF SEPTEMBER</Subtitle>
            <Divider />
            <Subtitle>HANA & DAVID'S WEDDING</Subtitle>
            <Subtitle>OCTOBER 11, 2026</Subtitle>
            <Divider />
          </Header>

          <Form onSubmit={handleSubmit}>
            {!selectedGroup && (
              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <FieldNote>
                  Start typing your name and select it from the list
                </FieldNote>
                <SearchWrapper ref={searchRef}>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={nameQuery}
                    onChange={handleQueryChange}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search for your name..."
                    autoComplete="off"
                  />
                  {showSuggestions && query && (
                    <SuggestionList>
                      {suggestions.length > 0 ? (
                        suggestions.map((entry, index) => (
                          <SuggestionItem
                            key={`${entry.groupIndex}-${entry.name}`}
                            $highlighted={index === highlightIndex}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectGroup(entry.groupIndex)}
                          >
                            {entry.name}
                            {guestGroups[entry.groupIndex].length > 1 && (
                              <SuggestionParty>
                                Party of {guestGroups[entry.groupIndex].length}
                              </SuggestionParty>
                            )}
                          </SuggestionItem>
                        ))
                      ) : (
                        <NoResults>
                          No matching name found. Please try another spelling.
                        </NoResults>
                      )}
                    </SuggestionList>
                  )}
                </SearchWrapper>
              </FormGroup>
            )}

            {selectedGroup && (
              <FormGroup>
                <PartyContainer>
                  <PartyHeader>
                    <PartyTitle>Your Party</PartyTitle>
                    <ChangeButton type="button" onClick={handleClearGroup}>
                      Not your party?
                    </ChangeButton>
                  </PartyHeader>
                  {selectedGroup.map(guestName => (
                    <GuestRow key={guestName}>
                      <GuestName>{guestName}</GuestName>
                      <RadioGroup style={{ marginTop: 0 }}>
                        <RadioLabel>
                          <input
                            type="radio"
                            name={`attendance-${guestName}`}
                            value="attending"
                            checked={guestResponses[guestName] === 'attending'}
                            onChange={() => handleGuestResponse(guestName, 'attending')}
                          />
                          Joyfully Accept
                        </RadioLabel>
                        <RadioLabel>
                          <input
                            type="radio"
                            name={`attendance-${guestName}`}
                            value="declining"
                            checked={guestResponses[guestName] === 'declining'}
                            onChange={() => handleGuestResponse(guestName, 'declining')}
                          />
                          Regretfully Decline
                        </RadioLabel>
                      </RadioGroup>
                    </GuestRow>
                  ))}
                </PartyContainer>
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="dietaryRestrictions">Dietary Restrictions</Label>
              <FieldNote>
                Please note that all food served will be in accordance with Glatt kosher dietary laws
              </FieldNote>
              <TextArea
                id="dietaryRestrictions"
                name="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={handleChange}
                placeholder="Please let us know of any dietary restrictions or allergies."
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message to the Couple</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Share your well wishes or a special message..."
              />
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit RSVP'}
            </SubmitButton>
          </Form>
        </FormContainer>
      </PageBorder>
    </AppContainer>
  );
}

export default RSVPPage;
